/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Cylinder, Text } from '@react-three/drei';
import type { Group } from 'three';

// Fix for React Three Fiber Intrinsic Elements in TypeScript
declare global {
  namespace JSX {
    interface IntrinsicElements {
      group: any;
      meshStandardMaterial: any;
    }
  }
}

export interface CoinModelProps {
    position?: [number, number, number];
    symbol?: string;
    color?: string;
}

export const CoinModel: React.FC<CoinModelProps> = ({ 
    position = [0, 0, 0], 
    symbol = "$",
    color = "#C5A059"
}) => {
  const ref = useRef<Group>(null);
  
  useFrame((state) => {
    if (ref.current) {
      const t = state.clock.getElapsedTime();
      // Gentle floating and rotation
      ref.current.position.y = position[1] + Math.sin(t * 1.5) * 0.1;
      ref.current.rotation.y = t * 0.5; // Constant spin
      ref.current.rotation.x = Math.sin(t * 0.5) * 0.1;
    }
  });

  return (
    <group ref={ref} position={position}>
      {/* Coin Edge (Ridges) */}
      <Cylinder args={[2, 2, 0.2, 128]} rotation={[Math.PI / 2, 0, 0]}>
        <meshStandardMaterial color={color} metalness={0.95} roughness={0.15} />
      </Cylinder>
      
      {/* Coin Face Front */}
      <Cylinder args={[1.9, 1.9, 0.22, 64]} rotation={[Math.PI / 2, 0, 0]}>
        <meshStandardMaterial color="#E5C069" metalness={0.8} roughness={0.1} />
      </Cylinder>

      {/* Decorative inner ring */}
      <Cylinder args={[1.6, 1.6, 0.23, 64]} rotation={[Math.PI / 2, 0, 0]}>
         <meshStandardMaterial color={color} metalness={0.9} roughness={0.2} />
      </Cylinder>

      {/* Symbol on Coin */}
      <Text
        position={[0, 0, 0.13]}
        fontSize={1.2}
        font="https://fonts.gstatic.com/s/playfairdisplay/v30/nuFvD-vYSZviVYUb_rj3ij__anPXJzDwcbmjWBN2PKdFvXDXbtM.woff"
        color="#FFFFFF"
        anchorX="center"
        anchorY="middle"
      >
        {symbol}
      </Text>
      
       <Text
        position={[0, 0, -0.13]}
        rotation={[0, Math.PI, 0]}
        fontSize={1.2}
        font="https://fonts.gstatic.com/s/playfairdisplay/v30/nuFvD-vYSZviVYUb_rj3ij__anPXJzDwcbmjWBN2PKdFvXDXbtM.woff"
        color="#FFFFFF"
        anchorX="center"
        anchorY="middle"
      >
        {symbol}
      </Text>
    </group>
  );
};